import { useCallback, useEffect, useState } from 'react';
import type { IModulePackageState } from '@platform/core/types/modularity/IModulePackageState';
import { useAppSelector } from '@platform/state/hooks';
import { isModuleLoaded, loadModule } from './loadModule';
import { findModule } from './moduleRegistry';

/**
 * Готовність пакета модуля для поточної мови: 'loading' поки вантажаться стан/i18n/представлення, 'ready' — готовий,
 * 'missing' — пакета нема (працює лише узагальнений рушій), 'error' — завантаження не вдалося (error + retry).
 */
export function useModulePackage(moduleKey: string | undefined): IModulePackageState {
  const lang = useAppSelector((s) => s.session.lang);
  const manifest = moduleKey ? findModule(moduleKey) : undefined;
  const [ready, setReady] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ key: string; error: unknown } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setFailure(null);
    setAttempt((n) => n + 1);
  }, []);
  useEffect(() => {
    if (!manifest) return;
    let cancelled = false;
    const key = `${manifest.key}|${lang}`;
    loadModule(manifest, lang).then(
      () => { if (!cancelled) setReady(key); },
      (error: unknown) => { if (!cancelled) setFailure({ key, error }); },
    );
    return () => {
      cancelled = true;
    };
  }, [manifest, lang, attempt]);
  if (!manifest) return { status: 'missing' };
  const key = `${manifest.key}|${lang}`;
  if (failure?.key === key) return { status: 'error', manifest, error: failure.error, retry };
  return { status: ready === key || isModuleLoaded(manifest, lang) ? 'ready' : 'loading', manifest };
}
